import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Bell, Check, Share2, CalendarDays, FileText, Info, Wifi, WifiOff, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const typeIcon = (t) => {
  const map = { share: Share2, appointment: CalendarDays, import: FileText, info: Info, warning: AlertTriangle };
  return map[t] || Info;
};

export default function NotificationCenter() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ws = useWebSocket();

  const fetchCount = useCallback(() => {
    api.getNotificationCount().then(r => setUnread(r.data.count)).catch(err => logger.error(err));
  }, []);

  const fetchAll = useCallback(() => {
    api.getNotifications().then(r => setNotifications(r.data)).catch(err => logger.error(err));
    fetchCount();
  }, [fetchCount]);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Listen to WebSocket events for live badge updates
  useEffect(() => {
    if (!ws?.addListener) return;
    const unsub = ws.addListener('any', () => {
      // On any WS event, refresh the count
      fetchCount();
    });
    return unsub;
  }, [ws, fetchCount]);

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  const handleMarkAllRead = async () => {
    await api.markAllRead();
    setUnread(0);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleClick = async (notif) => {
    if (!notif.read) {
      await api.markOneRead(notif.id);
      setUnread(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
    }
    if (notif.link) {
      setOpen(false);
      navigate(notif.link);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button className="relative p-1.5 rounded-sm hover:bg-slate-50 transition-all duration-200" data-testid="notification-bell">
          <Bell className="w-4.5 h-4.5 text-slate-600" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#0047AB] text-white text-[9px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="notification-badge">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
          {ws?.connected && <span className="absolute bottom-0 right-0 w-1.5 h-1.5 bg-emerald-400 rounded-full" data-testid="ws-connected-indicator" title="Live updates active" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notification-dropdown">
        <div className="p-3 border-b border-slate-200 flex items-center justify-between">
          <span className="font-heading font-semibold text-slate-900 text-sm">Notifications</span>
          {unread > 0 && (
            <Button variant="ghost" size="sm" className="h-6 text-xs text-[#0047AB]" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
              <Check className="w-3 h-3 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="py-8 text-center">
              <Bell className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No notifications yet</p>
            </div>
          ) : notifications.map(n => {
            const Icon = typeIcon(n.type);
            return (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 transition-colors flex items-start gap-2.5 ${!n.read ? 'bg-blue-50/40' : ''}`}
                data-testid={`notification-${n.id}`}
              >
                <div className={`w-7 h-7 rounded-sm flex items-center justify-center shrink-0 mt-0.5 ${n.type === 'warning' ? 'bg-amber-100 text-amber-600' : !n.read ? 'bg-[#EFF6FF] text-[#0047AB]' : 'bg-slate-100 text-slate-400'}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium truncate ${!n.read ? 'text-slate-900' : 'text-slate-600'}`}>{n.title}</p>
                  <p className="text-[11px] text-slate-500 line-clamp-2">{n.message}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                  {n.type === 'warning' && n.link?.includes('/leads/') && (
                    <span
                      role="button"
                      onClick={e => {
                        e.stopPropagation();
                        const leadId = n.link.split('/leads/')[1];
                        if (leadId) {
                          api.updateLead(leadId, { status: 'lost' })
                            .then(() => { toast.success('Lead marked as Lost'); setOpen(false); })
                            .catch(() => toast.error('Failed to update'));
                        }
                      }}
                      className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 rounded-sm hover:bg-red-200 transition-colors cursor-pointer"
                      data-testid={`mark-lost-${n.id}`}
                    >
                      Mark as Lost
                    </span>
                  )}
                </div>
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#0047AB] mt-2 shrink-0" />}
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
